import { colors } from 'quasar';
import type mapboxgl from 'mapbox-gl';
import type { Feature, FeatureCollection, GeoJSON, Geometry } from 'geojson';
import type { MapLayerPreview } from '@/components/map/mapLayer.types';

export const DEFAULT_POINT_RADIUS = 4;

const markerSizePresets: Record<string, number> = { small: 4, medium: 8, large: 12 };

export function normalizeMarkerSize(value: unknown): number | undefined {
  if (value == null) return undefined;
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    if (normalized in markerSizePresets) {
      return markerSizePresets[normalized];
    }
    const parsed = Number.parseFloat(normalized);
    if (!Number.isNaN(parsed)) {
      return parsed;
    }
  }
  return DEFAULT_POINT_RADIUS;
}

export function normalizeToFeatureCollection(input: GeoJSON | null | undefined): FeatureCollection {
  if (!input) return { type: 'FeatureCollection', features: [] };
  if (input.type === 'FeatureCollection') return input as FeatureCollection;
  if (input.type === 'Feature') return { type: 'FeatureCollection', features: [input as Feature] };
  const geometry = input as Geometry;
  if ('coordinates' in geometry && Array.isArray((geometry as { coordinates: unknown }).coordinates)) {
    return {
      type: 'FeatureCollection',
      features: [{ type: 'Feature', geometry, properties: {} }],
    };
  }
  return { type: 'FeatureCollection', features: [] };
}

export function overlaySourceId(id: number | string): string {
  return `custom-layer-${id}`;
}

export function overlayCircleId(id: number | string): string {
  return `custom-layer-${id}-circle`;
}

export function overlayLineId(id: number | string): string {
  return `custom-layer-${id}-line`;
}

export function overlayFillId(id: number | string): string {
  return `custom-layer-${id}-fill`;
}

export function overlayBorderId(id: number | string): string {
  return `custom-layer-${id}-border`;
}

export function overlayLayerIds(id: number | string): string[] {
  return [overlayLineId(id), overlayFillId(id), overlayBorderId(id), overlayCircleId(id)];
}

/**
 * Mapbox `to-number` coerces a missing property (null) to 0 and treats that as a successful
 * conversion, so the fallbacks passed to it are never reached. `coalesce` is what actually skips
 * nulls, so pick the value first and only then coerce it.
 */
function numberWithFallback(property: string, ...fallbacks: (string | number)[]): mapboxgl.ExpressionSpecification {
  const candidates = fallbacks.map((fallback) => (typeof fallback === 'string' ? ['get', fallback] : fallback));
  return ['to-number', ['coalesce', ['get', property], ...candidates]];
}

const lineFilter: mapboxgl.ExpressionSpecification = [
  'any',
  ['==', ['geometry-type'], 'LineString'],
  ['==', ['geometry-type'], 'MultiLineString'],
];
const polygonFilter: mapboxgl.ExpressionSpecification = [
  'any',
  ['==', ['geometry-type'], 'Polygon'],
  ['==', ['geometry-type'], 'MultiPolygon'],
];
const pointFilter: mapboxgl.ExpressionSpecification = [
  'any',
  ['==', ['geometry-type'], 'Point'],
  ['==', ['geometry-type'], 'MultiPoint'],
];

/** The four layers that together render one imported GeoJSON layer. */
function overlayLayerSpecs(layerId: number | string, sourceId: string, visibility: 'visible' | 'none') {
  const specs: mapboxgl.LayerSpecification[] = [
    {
      id: overlayLineId(layerId),
      type: 'line',
      source: sourceId,
      filter: lineFilter,
      paint: {
        'line-width': numberWithFallback('stroke-width', 2),
        'line-color': ['coalesce', ['get', 'stroke'], ['get', 'color'], '#1976d2'],
        'line-opacity': numberWithFallback('stroke-opacity', 'opacity', 0.9),
      },
      layout: { visibility },
    },
    {
      id: overlayFillId(layerId),
      type: 'fill',
      source: sourceId,
      filter: polygonFilter,
      paint: {
        'fill-color': ['coalesce', ['get', 'fill'], ['get', 'color'], '#1976d2'],
        'fill-opacity': numberWithFallback('fill-opacity', 'opacity', 0.3),
      },
      layout: { visibility },
    },
    {
      id: overlayBorderId(layerId),
      type: 'line',
      source: sourceId,
      filter: polygonFilter,
      paint: {
        'line-width': numberWithFallback('stroke-width', 2),
        'line-color': ['coalesce', ['get', 'stroke'], ['get', 'fill'], ['get', 'color'], '#0f4aa2'],
        'line-opacity': numberWithFallback('stroke-opacity', 'opacity', 1.0),
      },
      layout: { visibility },
    },
    {
      id: overlayCircleId(layerId),
      type: 'circle',
      source: sourceId,
      filter: pointFilter,
      paint: {
        'circle-radius': numberWithFallback('marker-size', DEFAULT_POINT_RADIUS),
        'circle-color': ['coalesce', ['get', 'marker-color'], ['get', 'color'], ['get', 'stroke'], '#1976d2'],
        'circle-opacity': numberWithFallback('marker-opacity', 'opacity', 0.95),
        'circle-stroke-color': ['coalesce', ['get', 'marker-stroke'], ['get', 'stroke'], '#0f4aa2'],
        'circle-stroke-width': numberWithFallback('marker-stroke-width', 1.5),
      },
      layout: { visibility },
    },
  ];
  return specs;
}

/**
 * Fills in the styling defaults an imported GeoJSON file usually omits, so the paint expressions
 * above have something to read.
 */
export function applyStylingDefaults(featureCollection: FeatureCollection): FeatureCollection {
  featureCollection.features = (featureCollection.features || []).map((feature) => {
    if (!feature || !feature.geometry) return feature;
    const geomType = feature.geometry.type;
    const props = feature.properties || {};

    if (geomType === 'Polygon' || geomType === 'MultiPolygon') {
      if (!props.stroke) {
        const base = props.fill || props.color || '#1976d2';
        try {
          props.stroke = colors.lighten(base, -20);
        } catch {
          props.stroke = base;
        }
      }
      if (props['stroke-width'] == null) props['stroke-width'] = 2;
      if (props['stroke-opacity'] == null) props['stroke-opacity'] = 1.0;
      if (props['fill-opacity'] == null) props['fill-opacity'] = props.opacity ?? 0.3;
    } else if (geomType === 'LineString' || geomType === 'MultiLineString') {
      if (props['stroke-width'] == null) props['stroke-width'] = 2;
      if (props['stroke-opacity'] == null) props['stroke-opacity'] = 0.9;
      if (!props.stroke && props.color) {
        props.stroke = props.color;
      }
    } else if (geomType === 'Point' || geomType === 'MultiPoint') {
      if (!props['marker-color']) {
        props['marker-color'] = props.color || props.stroke || '#1976d2';
      }
      if (props['marker-opacity'] == null) {
        props['marker-opacity'] = props.opacity ?? 0.95;
      }
      props['marker-size'] =
        normalizeMarkerSize(props['marker-size']) ?? normalizeMarkerSize(props.radius) ?? DEFAULT_POINT_RADIUS;
      if (props['marker-stroke'] == null) {
        props['marker-stroke'] = props.stroke || '#0f4aa2';
      }
      if (props['marker-stroke-width'] == null) {
        props['marker-stroke-width'] = 1.5;
      }
    }

    feature.properties = props;
    return feature;
  });
  return featureCollection;
}

export async function loadAndPrepareGeoJson(input: string | GeoJSON): Promise<FeatureCollection | null> {
  try {
    const data: GeoJSON | null =
      typeof input === 'string'
        ? await fetch(input).then((response) => (response.ok ? (response.json() as Promise<GeoJSON>) : null))
        : input;
    if (!data) return null;
    return applyStylingDefaults(normalizeToFeatureCollection(data));
  } catch (error) {
    console.debug('loadAndPrepareGeoJson error', error);
    return null;
  }
}

export async function addGeoJsonOverlay(map: mapboxgl.Map | null, layer: MapLayerPreview): Promise<void> {
  if (!map || !layer?.file?.url) return;
  const sid = overlaySourceId(layer.id);
  const data = await loadAndPrepareGeoJson(layer.file.url);
  if (!data) return;

  if (!map.getSource(sid)) {
    map.addSource(sid, { type: 'geojson', data } as mapboxgl.GeoJSONSourceSpecification);
  } else {
    const source = map.getSource(sid) as mapboxgl.GeoJSONSource | undefined;
    source?.setData(data);
  }

  const visibility = layer.visible === false ? 'none' : 'visible';
  for (const spec of overlayLayerSpecs(layer.id, sid, visibility)) {
    if (map.getLayer(spec.id)) {
      map.setLayoutProperty(spec.id, 'visibility', visibility);
    } else {
      map.addLayer(spec);
    }
  }
}

export function removeGeoJsonOverlay(map: mapboxgl.Map | null, layerId: number | string): void {
  if (!map) return;
  for (const id of overlayLayerIds(layerId)) {
    if (map.getLayer(id)) {
      map.removeLayer(id);
    }
  }
  const sid = overlaySourceId(layerId);
  if (map.getSource(sid)) {
    map.removeSource(sid);
  }
}

export function setGeoJsonOverlayVisibility(map: mapboxgl.Map | null, layerId: number | string, visible: boolean) {
  if (!map) return;
  for (const id of overlayLayerIds(layerId)) {
    if (map.getLayer(id)) {
      map.setLayoutProperty(id, 'visibility', visible ? 'visible' : 'none');
    }
  }
}
